import { Box, Container, Typography, Link as MuiLink, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const SECTIONS = [
  { id: 'who-we-are', title: 'Who we are' },
  { id: 'what-data-we-collect', title: 'What data we collect and why' },
  { id: 'lawful-basis', title: 'Lawful basis' },
  { id: 'who-we-share-data-with', title: 'Who we share data with' },
  { id: 'how-long-we-keep-data', title: 'How long we keep your data' },
  { id: 'your-rights', title: 'Your rights' },
  { id: 'security', title: 'Security' },
  { id: 'changes', title: 'Changes' },
];

export default function PrivacyPolicy() {
  return (
    <Box sx={{ py: 4, px: 2, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Privacy Notice
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last updated: March 2026
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Contents
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            {SECTIONS.map(({ id, title }, i) => (
              <li key={id}>
                <MuiLink href={`#${id}`} underline="hover" color="primary" sx={{ fontSize: '0.8125rem' }}>
                  {title}
                </MuiLink>
              </li>
            ))}
          </Box>
        </Paper>

        <Typography id="who-we-are" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          1. Who we are
        </Typography>
        <Typography variant="body2" paragraph>
          Galldris Core is an in-house application operated by Galldris (the company) for managing projects, inductions, safety reporting, and related activities. Galldris is the data controller for the personal data processed in this application. If you have questions about this notice or your data, please contact your usual Galldris contact or the data protection contact identified in your contract or internal communications.
        </Typography>

        <Typography id="what-data-we-collect" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          2. What data we collect and why
        </Typography>
        <Typography variant="body2" paragraph>
          We process personal data necessary to provide and operate the Galldris Core platform. This includes:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li><strong>Account and identity:</strong> name, email address, phone number, profile photo, role, and authentication data (e.g. hashed passwords, session tokens). We use this to create and manage your account, identify you, and secure access.</li>
          <li><strong>Employment and contact details:</strong> job role, company, employment type, address, emergency contact. We use this for inductions, project assignment, and safety-related communication.</li>
          <li><strong>Certificates and competencies:</strong> certificate type, document numbers, expiry dates, and copies of certificates where uploaded. We use this to verify qualifications and compliance for site and project access.</li>
          <li><strong>Induction and safety data:</strong> completion of company and site inductions, health and medical assessments (where required for safety), and declarations. We use this to meet health and safety and contractual requirements.</li>
          <li><strong>Safety reporting:</strong> good calls, close calls, incidents, and related comments, attachments, and assignments. We may store your name or user reference as the reporter or assignee where relevant.</li>
          <li><strong>RAMS and briefings:</strong> documents you upload, briefing attendance, and signatures. We use this for document control and compliance.</li>
          <li><strong>Facial recognition (if enabled):</strong> biometric data used to verify your identity via an external service. We use this only where deployed for authentication or attendance.</li>
          <li><strong>Usage and audit:</strong> access logs (e.g. IP address, user agent, actions) for security and audit. We use this to protect the system and investigate incidents.</li>
        </Typography>

        <Typography id="lawful-basis" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          3. Lawful basis
        </Typography>
        <Typography variant="body2" paragraph>
          We process your data on the following bases under UK GDPR / GDPR:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li><strong>Contract:</strong> where processing is necessary to perform our contract with you or your employer (e.g. providing account access, site induction, project assignment).</li>
          <li><strong>Legal obligation:</strong> where we must process data to comply with law (e.g. health and safety, tax, or regulatory requirements).</li>
          <li><strong>Legitimate interest:</strong> where we have a legitimate interest that is not overridden by your rights (e.g. security, audit, improving safety, managing projects).</li>
          <li><strong>Consent:</strong> where we rely on consent (e.g. for optional features or marketing), we will ask you separately and you may withdraw at any time.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          Where we process special category data (e.g. health data in medical assessments, or biometric data for facial recognition), we do so only where a specific legal condition under data protection law applies (e.g. employment, health and safety, or consent where appropriate).
        </Typography>

        <Typography id="who-we-share-data-with" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          4. Who we share data with
        </Typography>
        <Typography variant="body2" paragraph>
          We may share your data with:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li><strong>Service providers (processors):</strong> e.g. hosting, email delivery, and (if used) facial recognition or verification APIs. They process data on our instructions and under contract.</li>
          <li><strong>Other group companies or project partners:</strong> where necessary for the same purposes (e.g. project and safety management).</li>
          <li><strong>Regulators or law enforcement:</strong> when required by law or to protect our legal rights.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          We do not sell your personal data.
        </Typography>

        <Typography id="how-long-we-keep-data" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          5. How long we keep your data
        </Typography>
        <Typography variant="body2" paragraph>
          We keep your data only as long as necessary for the purposes above or as required by law. For example:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li>Account and profile data: while your account is active and for a defined period after last use or after you leave (as set out in our retention policy).</li>
          <li>Induction and safety records: for the duration of the project and for a number of years thereafter for legal and safety reasons.</li>
          <li>Access and audit logs: for a limited period (e.g. 12–24 months) unless we need them longer for investigations or legal claims.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          After the retention period, we delete or anonymise your data so it can no longer identify you.
        </Typography>

        <Typography id="your-rights" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          6. Your rights
        </Typography>
        <Typography variant="body2" paragraph>
          Under UK GDPR / GDPR you have the right to:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li><strong>Access:</strong> request a copy of the personal data we hold about you.</li>
          <li><strong>Rectification:</strong> ask us to correct inaccurate or incomplete data.</li>
          <li><strong>Erasure:</strong> ask us to delete your data in certain circumstances (&quot;right to be forgotten&quot;).</li>
          <li><strong>Restriction:</strong> ask us to limit how we use your data in certain cases.</li>
          <li><strong>Data portability:</strong> receive your data in a structured, machine-readable format where the lawful basis is contract or consent.</li>
          <li><strong>Object:</strong> object to processing based on legitimate interest, including profiling.</li>
          <li><strong>Withdraw consent:</strong> where we rely on consent, you may withdraw it at any time.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          To exercise these rights, contact the data protection contact provided by Galldris (e.g. in your contract or on the intranet). You may also use the &quot;Download my data&quot; and &quot;Delete my account&quot; options in the application where available. You have the right to lodge a complaint with the supervisory authority (in the UK, the ICO).
        </Typography>

        <Typography id="security" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          7. Security
        </Typography>
        <Typography variant="body2" paragraph>
          We use appropriate technical and organisational measures to protect your data (e.g. access controls, encryption, secure sessions). Access to personal data is limited to those who need it for the purposes above.
        </Typography>

        <Typography id="changes" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          8. Changes
        </Typography>
        <Typography variant="body2" paragraph>
          We may update this notice from time to time. We will post the updated version here and, where appropriate, notify you of significant changes.
        </Typography>

        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <MuiLink component={Link} to="/login" underline="hover" sx={{ mr: 2 }}>
            Back to login
          </MuiLink>
          <MuiLink component={Link} to="/cookie-policy" underline="hover">
            Cookie Policy
          </MuiLink>
        </Box>
      </Container>
    </Box>
  );
}
